import React, { useEffect, useState } from "react";
import {
  Wand2,
  Brush,
  Image as ImageIco,
  SlidersHorizontal,
  Images,
} from "lucide-react";
import { toast } from "sonner";
import { useImageStudio } from "../../context/ImageStudioContext";
import { StudioAsset } from "../../service/apiService";
import { withDisclosureLabel, disclosureTextFor } from "../../utils/exportImage";
import ControlsPanel from "./ControlsPanel";
import Canvas from "./Canvas";
import MaskCanvas from "./MaskCanvas";
import AssetPanel from "./AssetPanel";
import GallerySwitcher from "./GallerySwitcher";

type Pane = "controls" | "canvas" | "assets";

const DISCLOSURE_KEY = "kyva-studio-disclosure-label";

const ImageStudio: React.FC = () => {
  const {
    result,
    results,
    selectResult,
    clearResult,
    variants,
    generating,
    loadAsInput,
    applySettings,
    currentSpec,
    referencePreviewUrl,
    setMaskB64,
    presetNeedsMask,
    activeGalleryId,
    assets,
    tools,
    presets,
  } = useImageStudio();

  // Disclosure label on download — defaults ON (the compliance-safe choice);
  // the user's last choice is remembered.
  const [disclosureOn, setDisclosureOn] = useState<boolean>(
    () => window.localStorage.getItem(DISCLOSURE_KEY) !== "0"
  );
  const toggleDisclosure = (on: boolean) => {
    setDisclosureOn(on);
    window.localStorage.setItem(DISCLOSURE_KEY, on ? "1" : "0");
  };

  // Which pane is shown on small screens (<lg). Ignored on lg+ where all show.
  const [pane, setPane] = useState<Pane>("controls");

  // A gallery asset can be previewed on the canvas; a fresh generation always
  // takes over the canvas, so clear the preview whenever `result` changes.
  const [viewed, setViewed] = useState<StudioAsset | null>(null);
  useEffect(() => {
    if (result) setViewed(null);
  }, [result]);

  // The previewed asset belongs to one gallery — never carry it across a
  // gallery switch, and drop it if it gets deleted from the asset list.
  useEffect(() => {
    setViewed(null);
  }, [activeGalleryId]);
  useEffect(() => {
    setViewed((v) => (v && !assets.some((a) => a.asset_id === v.asset_id) ? null : v));
  }, [assets]);

  // Mask is needed by the brush tools, and by presets whose mode requires one
  // (e.g. Virtual Staging "furnish" → Inpaint, paint where furniture goes).
  const maskTool = !!currentSpec?.needs_mask || presetNeedsMask;
  const inMaskMode = maskTool && !!referencePreviewUrl;

  // Toggle between painting the mask and viewing the latest result.
  const [previewResult, setPreviewResult] = useState(false);
  useEffect(() => {
    setPreviewResult(false);
  }, [referencePreviewUrl, currentSpec?.key]);
  useEffect(() => {
    if (result && inMaskMode) setPreviewResult(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  // On small screens, surface the canvas when work starts or finishes.
  useEffect(() => {
    if (generating || result) setPane("canvas");
  }, [generating, result]);

  const showMask = inMaskMode && !previewResult;

  const displayUrl = viewed?.presigned_url || result?.url || null;
  const displaySeed = viewed ? viewed.seed : result?.seed;
  const displayFormat = viewed?.output_format || result?.outputFormat || "png";
  const displayId = viewed?.asset_id || result?.assetId || "image";
  // Only AI output gets a disclosure label — never the user's own uploads.
  const displayIsGenerated = viewed ? viewed.asset_type === "generated" : !!result;
  const displayTool = viewed ? viewed.tool : result?.tool;

  // The stored generation recipe of whatever is displayed. A fresh result is
  // also in `assets` (refreshed right after generating), so its full params
  // come from there.
  const displayedAsset =
    viewed ?? (result ? assets.find((a) => a.asset_id === result.assetId) ?? null : null);
  const details =
    displayedAsset && displayedAsset.asset_type === "generated"
      ? {
          toolLabel:
            tools.find((t) => t.key === displayedAsset.tool)?.label ||
            presets.find((p) => p.key === displayedAsset.tool)?.label ||
            displayedAsset.tool ||
            "Unknown",
          prompt: displayedAsset.prompt || displayedAsset.params?.prompt,
          negativePrompt: displayedAsset.params?.negative_prompt,
          seed: displayedAsset.seed,
          params: displayedAsset.params || {},
          createdAt: displayedAsset.created_at,
        }
      : null;

  const handleClear = () => {
    if (viewed) {
      setViewed(null); // close the gallery preview (falls back to the result)
    } else {
      clearResult();
    }
  };

  const handleViewAsset = (asset: StudioAsset) => {
    setViewed(asset);
    if (inMaskMode) setPreviewResult(true);
    setPane("canvas");
  };

  const handleDownload = async () => {
    if (!displayUrl) return;
    try {
      const res = await fetch(displayUrl);
      let blob = await res.blob();
      if (disclosureOn && displayIsGenerated) {
        blob = await withDisclosureLabel(blob, disclosureTextFor(displayTool));
      }
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `kyva-${displayId}.${displayFormat}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Download failed — check the bucket's CORS configuration.");
    }
  };

  const handleUseAsInput = () => {
    const src = viewed
      ? { s3_key: viewed.s3_key, presigned_url: viewed.presigned_url, asset_id: viewed.asset_id }
      : result
      ? { s3_key: result.s3Key, presigned_url: result.url, asset_id: result.assetId }
      : null;
    if (!src) return;
    loadAsInput(src);
    setPane("controls");
  };

  const paneCls = (p: Pane, base: string) =>
    `${pane === p ? "flex" : "hidden"} lg:flex ${base}`;

  return (
    <div className="flex h-screen w-full flex-col bg-surface text-primary">
      {/* Header: title + gallery switcher */}
      <header className="flex items-center gap-2 border-b border-border-default px-3 py-2.5 sm:gap-3 sm:px-4">
        <div className="flex items-center gap-2">
          <Wand2 size={18} className="shrink-0 text-violet-500" />
          <h1 className="hidden text-sm font-semibold sm:block">Image Studio</h1>
        </div>
        <div className="mx-1 hidden h-5 w-px bg-border-default sm:block" />
        <div className="min-w-0 flex-1">
          <GallerySwitcher />
        </div>
      </header>

      {/* Zones */}
      <div className="flex min-h-0 flex-1">
        <aside
          className={paneCls(
            "controls",
            "w-full shrink-0 flex-col border-r border-border-default bg-surface-overlay lg:w-80"
          )}
        >
          <ControlsPanel />
        </aside>

        <main className={paneCls("canvas", "min-w-0 flex-1 flex-col bg-surface")}>
          {inMaskMode && (
            <div className="flex items-center justify-center gap-1 border-b border-border-default py-1.5">
              <button
                type="button"
                onClick={() => setPreviewResult(false)}
                className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium ${
                  !previewResult ? "bg-surface-2 text-primary" : "text-muted hover:text-primary"
                }`}
              >
                <Brush size={13} /> Edit mask
              </button>
              <button
                type="button"
                onClick={() => setPreviewResult(true)}
                disabled={!result && !viewed}
                className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium disabled:opacity-40 ${
                  previewResult ? "bg-surface-2 text-primary" : "text-muted hover:text-primary"
                }`}
              >
                <ImageIco size={13} /> View result
              </button>
            </div>
          )}

          <div className="min-h-0 flex-1">
            {showMask ? (
              <MaskCanvas imageUrl={referencePreviewUrl!} onMaskChange={setMaskB64} />
            ) : (
              <Canvas
                imageUrl={displayUrl}
                seed={displaySeed}
                generating={generating}
                generatingCount={variants}
                onDownload={handleDownload}
                onUseAsInput={handleUseAsInput}
                onClear={handleClear}
                variants={results}
                selectedId={viewed ? null : result?.assetId}
                onSelectVariant={selectResult}
                showDisclosureToggle={displayIsGenerated}
                disclosureOn={disclosureOn}
                onToggleDisclosure={toggleDisclosure}
                details={details}
                onApplySettings={
                  displayedAsset
                    ? () => {
                        applySettings(displayedAsset);
                        setPane("controls");
                      }
                    : undefined
                }
              />
            )}
          </div>
        </main>

        <aside
          className={`${pane === "assets" ? "block" : "hidden"} w-full shrink-0 border-l border-border-default bg-surface-overlay lg:block lg:w-64`}
        >
          <AssetPanel onView={handleViewAsset} />
        </aside>
      </div>

      {/* Bottom tab bar (small screens only) */}
      <nav className="flex shrink-0 border-t border-border-default bg-surface-overlay lg:hidden">
        {([
          { key: "controls", label: "Controls", Icon: SlidersHorizontal },
          { key: "canvas", label: "Canvas", Icon: ImageIco },
          { key: "assets", label: "Assets", Icon: Images },
        ] as { key: Pane; label: string; Icon: typeof ImageIco }[]).map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setPane(key)}
            className={`flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-medium ${
              pane === key ? "text-violet-500" : "text-muted"
            }`}
          >
            <Icon size={18} />
            {label}
          </button>
        ))}
      </nav>
    </div>
  );
};

export default ImageStudio;
